import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { LOGO_ICON_DATA_URI } from '../../assets/logoIcon'
import { isPublicPath } from '../../lib/publicPaths'
import { isPlainLoginVisit, loginPathFor } from '../../lib/loginRedirect'
import { returnPathFromSearch } from '../../lib/authReturn'

/**
 * AuthGate wraps the entire app.
 *
 * Behaviour matrix:
 * ┌──────────────────────────────┬──────────────────────────────┐
 * │ State                        │ Result                       │
 * ├──────────────────────────────┼──────────────────────────────┤
 * │ Public path (/login, /logout)│ Render children (passthrough)│
 * │ Plain /login, signed in      │ Go to ?next= (or home)       │
 * │ Loading                      │ Full-screen skeleton         │
 * │ Authenticated                │ Render children              │
 * │ Unauthenticated (open mode)  │ Go to /login (setup form)    │
 * │ Unauthenticated              │ Go to /login?next=<this page>│
 * └──────────────────────────────┴──────────────────────────────┘
 *
 * First-run setup is deliberately not a forced redirect on every load (signup
 * lands there once; see postSignupPath). Home gives a
 * compact, resumable entry point to the one authoritative /setup flow, so a
 * responder can still get to the work that brought them here.
 *
 * Open mode (no users exist yet) no longer bypasses auth. The LoginPage
 * detects open mode and shows the first-run setup form automatically.
 *
 * REG-398: a signed-out visitor is redirected to /login (not shown the form in
 * place), so the address bar says /login and sign-in returns them to the page
 * they asked for.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { loading, authenticated } = useAuth()
  const { pathname, search } = useLocation()

  // Always let public pages render, even before the auth check completes.
  if (isPublicPath(pathname)) {
    // Signed in and on a plain /login: nothing to do there. Anything the login
    // page must act on (reset, handoff, setup link, error) is left alone.
    if (pathname === '/login' && !loading && authenticated && isPlainLoginVisit(search)) {
      return <Navigate to={returnPathFromSearch(search)} replace />
    }
    return <>{children}</>
  }

  if (loading) {
    return <AuthLoadingScreen />
  }

  if (authenticated) {
    return <>{children}</>
  }

  // Not authenticated: go to /login, remembering the page they wanted.
  return <Navigate to={loginPathFor(pathname, search)} replace />
}

function AuthLoadingScreen() {
  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ backgroundColor: '#F1F5F9' }}
    >
      <div className="flex flex-col items-center gap-4">
        <div className="relative flex items-center justify-center">
          <div
            className="absolute inset-0 rounded-full opacity-20 animate-ping scale-125"
            style={{ backgroundColor: '#f55609' }}
          />
          <img src={LOGO_ICON_DATA_URI} alt="FluidifyAI Regen" className="relative w-10 h-10" />
        </div>
        <div className="text-sm font-bold tracking-tight text-[#1E293B]">FluidifyAI Regen</div>
      </div>
    </div>
  )
}
